import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../api/api";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import Spinner from "../components/common/Spinner";

export default function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart, clearCart } = useCart();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Estados para o Frete
  const [cep, setCep] = useState("");
  const [freteOptions, setFreteOptions] = useState([]);
  const [calculandoFrete, setCalculandoFrete] = useState(false);
  const [freteError, setFreteError] = useState("");

  useEffect(() => {
    async function loadProduct() {
      try {
        const res = await api.get(`/products/details/${id}`);
        setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    loadProduct();
    window.scrollTo(0, 0);
  }, [id]);

  const handleCalcularFrete = async () => {
    if (!cep || cep.length < 8) {
      setFreteError("Digite um CEP válido com 8 números.");
      return;
    }

    setCalculandoFrete(true);
    setFreteError("");
    setFreteOptions([]);

    try {
      // ✅ Chamada para o seu NOVO backend de frete regional
      const res = await api.post("/frete/calcular", {
        cepDestino: cep,
        weight: data?.product?.weight || "0.500"
      });

      // O seu backend agora devolve um Array direto do Banco de Dados
      // No formato: [{ Codigo: '04510', Valor: '15,50', PrazoEntrega: '5', ... }]
      if (res.data && Array.isArray(res.data)) {
        setFreteOptions(res.data);
      } else {
        setFreteError("Nenhuma opção de entrega para este CEP.");
      }

    } catch (err) {
      console.error("Erro no cálculo regional:", err);
      const msg = err.response?.data?.message || "Frete não disponível para esta região.";
      setFreteError(msg);
    } finally {
      setCalculandoFrete(false);
    }
  };

  const handleBuy = () => {
    if (!user) return navigate("/login");
    clearCart();
    addToCart({
      id: data.product.id,
      title: data.product.name,
      price: data.product.price,
      cover_image_url: data.product.image_url,
      type: 'product'
    });
    navigate("/checkout");
  };

  if (loading) return <Spinner />;
  if (!data?.product) return <div className="text-center py-20">Produto não encontrado.</div>;

  const { product, related } = data;

  return (
    <div className="container mx-auto px-6 py-10">
      <div className="grid md:grid-cols-2 gap-12 mb-20">
        {/* Imagem */}
        <div className="bg-white rounded-3xl p-4 shadow-sm border">
          <img src={product.image_url} alt={product.name} className="w-full h-auto rounded-2xl object-cover" />
        </div>

        {/* Info */}
        <div className="flex flex-col justify-center">
          <nav className="text-sm text-gray-400 mb-4">Loja / {product.name}</nav>
          <h1 className="text-4xl font-black text-gray-900 mb-4">{product.name}</h1>
          <p className="text-gray-600 mb-8 leading-relaxed">
            {product.description || "Descrição detalhada do produto."}
          </p>

          <div className="mb-8">
             {product.old_price && <span className="text-gray-400 line-through block text-lg">R$ {product.old_price}</span>}
             <span className="text-4xl font-black text-emerald-600 italic">R$ {product.price}</span>
          </div>

          {/* BOX DE FRETE REGIONAL */}
          <div className="mb-8 p-6 bg-gray-50 rounded-[2rem] border-2 border-dashed border-emerald-100">
            <h4 className="text-[10px] font-black text-emerald-800 uppercase tracking-widest mb-3 text-center">Simular Frete Regional</h4>
            <div className="flex gap-2">
              <input 
                type="text" 
                maxLength="8"
                placeholder="Digite seu CEP"
                className="flex-grow p-3 rounded-xl border-2 border-white focus:border-emerald-500 outline-none transition-all text-sm font-bold shadow-sm"
                value={cep}
                onChange={(e) => setCep(e.target.value.replace(/\D/g, ""))}
              />
              <button 
                onClick={handleCalcularFrete}
                disabled={calculandoFrete}
                className="px-6 py-3 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase hover:bg-emerald-700 transition-all disabled:opacity-50 shadow-md"
              >
                {calculandoFrete ? "..." : "Calcular"}
              </button>
            </div>

            {freteError && (
              <div className="mt-3 p-2 bg-red-50 rounded-lg border border-red-100">
                <p className="text-red-500 text-[10px] text-center font-bold uppercase">{freteError}</p>
              </div>
            )}

            {freteOptions.length > 0 && (
              <div className="mt-4 space-y-2 animate-slideDown">
                {freteOptions.map((opt, idx) => (
                  <div key={idx} className="flex justify-between items-center bg-white p-4 rounded-2xl shadow-sm border border-emerald-50">
                    <div className="flex flex-col">
                      <span className="text-[11px] font-black text-gray-800 uppercase tracking-tighter">
                        {opt.Codigo === '04014' || opt.Codigo === '4014' ? '🚀 Entrega Expressa' : '📦 Entrega Padrão'}
                      </span>
                      <span className="text-[9px] text-gray-400 font-bold">Prazo: {opt.PrazoEntrega} dias úteis</span>
                    </div>
                    <div className="text-right">
                      <span className="block text-lg font-black text-emerald-600">R$ {opt.Valor}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button 
            onClick={handleBuy}
            className="w-full md:w-max px-12 py-5 bg-gray-900 text-white rounded-2xl font-bold uppercase tracking-widest hover:bg-emerald-600 transition-all shadow-xl active:scale-95 flex items-center justify-center gap-3"
          >
            <span className="text-xl">🛒</span> Comprar Agora
          </button>
        </div>
      </div>

      {/* Sugestões */}
      {related?.length > 0 && (
        <section className="border-t pt-16">
          <h2 className="text-2xl font-black mb-8 uppercase italic">Quem viu este, também gostou:</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {related.map(item => (
              <Link key={item.id} to={`/produto/${item.id}`} className="group">
                <div className="bg-gray-50 rounded-2xl p-3 mb-3 overflow-hidden">
                  <img src={item.image_url} alt={item.name} className="w-full aspect-square object-cover rounded-xl group-hover:scale-105 transition-transform" />
                </div>
                <h3 className="font-bold text-gray-800 text-sm line-clamp-1">{item.name}</h3>
                <span className="text-emerald-600 font-bold">R$ {item.price}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}